import { create } from "zustand";
import type { Team } from "@/types/task";

interface TeamStore {
  // Team state
  showCreateTeam: boolean;
  editingTeam: Team | null;
  newTeamData: { name: string; description: string };
  validationError: string | null;

  // Actions
  setShowCreateTeam: (show: boolean) => void;
  setEditingTeam: (team: Team | null) => void;
  setNewTeamData: (data: { name: string; description: string }) => void;
  setValidationError: (error: string | null) => void;

  // Composite actions
  startCreateTeam: () => void;
  startEditTeam: (team: Team) => void;
  cancelTeamForm: () => void;
  resetTeamForm: () => void;
  validateTeamData: () => boolean;

  // Team state for breadcrumbs
  getTeamState: () => { showCreateTeam: boolean; editingTeam: Team | null };
}

export const useTeamStore = create<TeamStore>((set, get) => ({
  // Initial state
  showCreateTeam: false,
  editingTeam: null,
  newTeamData: { name: "", description: "" },
  validationError: null,

  // Basic setters
  setShowCreateTeam: (show) => set({ showCreateTeam: show }),
  setEditingTeam: (team) => set({ editingTeam: team }),
  setNewTeamData: (data) => set({ newTeamData: data }),
  setValidationError: (error) => set({ validationError: error }),

  // Composite actions
  startCreateTeam: () =>
    set({
      showCreateTeam: true,
      editingTeam: null,
      newTeamData: { name: "", description: "" },
      validationError: null,
    }),

  startEditTeam: (team) =>
    set({
      showCreateTeam: false,
      editingTeam: team,
      newTeamData: {
        name: team.name,
        description: team.description || "",
      },
      validationError: null,
    }),

  cancelTeamForm: () =>
    set({
      showCreateTeam: false,
      editingTeam: null,
      newTeamData: { name: "", description: "" },
      validationError: null,
    }),

  resetTeamForm: () =>
    set({
      showCreateTeam: false,
      editingTeam: null,
      newTeamData: { name: "", description: "" },
      validationError: null,
    }),

  // Validation function
  validateTeamData: () => {
    const { newTeamData } = get();
    if (!newTeamData.name.trim()) {
      set({ validationError: "Team name is required" });
      return false;
    }
    if (newTeamData.name.trim().length > 50) {
      set({ validationError: "Team name must be 50 characters or less" });
      return false;
    }
    set({ validationError: null });
    return true;
  },

  // Getter for breadcrumb integration
  getTeamState: () => {
    const { showCreateTeam, editingTeam } = get();
    return { showCreateTeam, editingTeam };
  },
}));
